import React, { useState } from "react";
import { Helmet } from "react-helmet";

const FEATURES = [
  {
    id: "monitor",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    title: "Multi-Platform Review Monitoring",
    description: "Track and monitor reviews from Google Business Profile, Yelp, Facebook, Trustpilot, G2, Capterra, and dozens more platforms in a single unified dashboard. Get instant alerts when new reviews are posted so you never miss customer feedback.",
    benefits: ["Real-time review alerts", "50+ platform support", "Unified inbox for all reviews", "Custom notification rules"]
  },
  {
    id: "sentiment",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: "AI-Powered Sentiment Analysis",
    description: "Leverage advanced natural language processing to automatically analyze the sentiment, tone, and key themes within your customer reviews. Identify trends, detect emerging issues, and understand what customers truly feel about your brand.",
    benefits: ["Positive, negative, neutral classification", "Keyword and theme extraction", "Trend analysis over time", "Competitor sentiment comparison"]
  },
  {
    id: "respond",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    title: "AI-Generated Review Responses",
    description: "Generate professional, personalized review responses in seconds using AI that matches your brand voice. Respond to both positive and negative reviews with contextually appropriate messages that strengthen customer relationships and protect your reputation.",
    benefits: ["Brand voice customization", "One-click response generation", "Multi-language support", "Response templates library"]
  },
  {
    id: "reputation",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    title: "Reputation Score Tracking",
    description: "Monitor your overall reputation score across all platforms with a comprehensive scoring algorithm. Track improvements over time, set reputation goals, and benchmark against competitors to ensure your brand maintains a strong online presence.",
    benefits: ["Aggregate reputation score", "Historical trend tracking", "Competitor benchmarking", "Goal setting and alerts"]
  },
  {
    id: "campaigns",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: "Review Generation Campaigns",
    description: "Create automated email and SMS campaigns to encourage satisfied customers to leave reviews on the platforms that matter most. Use smart timing, personalized messages, and follow-up sequences to maximize your review acquisition rate.",
    benefits: ["Email and SMS campaigns", "Smart timing optimization", "QR code generation", "A/B testing for messages"]
  },
  {
    id: "reporting",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    title: "Advanced Analytics and Reporting",
    description: "Access detailed reports on review volume, sentiment trends, response times, and platform performance. Generate white-label reports for clients, schedule automated report delivery, and gain actionable insights to improve your online reputation strategy.",
    benefits: ["Custom report builder", "Scheduled report delivery", "White-label client reports", "ROI tracking dashboard"]
  }
];

const PLATFORMS = [
  { name: "Google Business Profile", color: "#4285F4" },
  { name: "Yelp", color: "#D32323" },
  { name: "Facebook", color: "#1877F2" },
  { name: "Trustpilot", color: "#00B67A" },
  { name: "G2", color: "#FF492C" },
  { name: "Capterra", color: "#FF9D28" },
  { name: "TripAdvisor", color: "#34E0A1" },
  { name: "BBB", color: "#005A78" },
  { name: "Apple Maps", color: "#000000" },
  { name: "Glassdoor", color: "#0CAA41" },
  { name: "Amazon", color: "#FF9900" },
  { name: "Angi", color: "#FF6138" }
];

const FAQ_ITEMS = [
  {
    q: "What review platforms does SERPfinity Review Management support?",
    a: "SERPfinity Review Management will support over 50 review platforms including Google Business Profile, Yelp, Facebook, Trustpilot, G2, Capterra, TripAdvisor, BBB, Glassdoor, Amazon, and many more. All reviews are aggregated into a single unified dashboard for easy management."
  },
  {
    q: "How does AI-powered sentiment analysis work for reviews?",
    a: "Our AI sentiment analysis uses advanced natural language processing to automatically classify reviews as positive, negative, or neutral. It also extracts key themes, identifies trending topics, and detects emerging issues so you can proactively address customer concerns before they escalate."
  },
  {
    q: "Can I generate AI responses to customer reviews?",
    a: "Yes, SERPfinity Review Management includes AI-powered response generation that creates professional, personalized replies matching your brand voice. You can respond to reviews across all platforms from a single dashboard, with one-click generation and customizable templates."
  },
  {
    q: "How does the review generation campaign feature work?",
    a: "Review generation campaigns allow you to create automated email and SMS sequences that encourage satisfied customers to leave reviews. You can customize timing, messaging, and target platforms. The system includes smart follow-up sequences and A/B testing to maximize your review acquisition rate."
  },
  {
    q: "Will Review Management integrate with my existing SERPfinity tools?",
    a: "Absolutely. Review Management is designed to work seamlessly with all existing SERPfinity tools including Content Audit, SERP Analysis, AI Search Visibility, and more. Review data will feed into your overall SEO and reputation insights for a comprehensive view of your online presence."
  },
  {
    q: "When will Review Management be available?",
    a: "Review Management is currently in active development and will be launching soon. Join the early access waitlist to be among the first to try it and receive exclusive launch pricing. We will notify you by email as soon as the feature is ready."
  }
];

const ReviewManagement = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleNotify = (e) => {
    e.preventDefault();
    if (email) setSubmitted(true);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SERPfinity Review Management",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "AI-powered review management platform for monitoring, analyzing, and responding to customer reviews across 50+ platforms. Track reputation scores, generate AI responses, and run review acquisition campaigns.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/PreOrder",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Multi-platform review monitoring",
      "AI-powered sentiment analysis",
      "AI-generated review responses",
      "Reputation score tracking",
      "Review generation campaigns",
      "Advanced analytics and reporting"
    ],
    "creator": {
      "@type": "Organization",
      "name": "SERPfinity",
      "url": "https://thedigitalhall.club"
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Review Management - AI-Powered Reputation Monitoring | SERPfinity</title>
        <meta name="description" content="Monitor, analyze, and respond to customer reviews across 50+ platforms with AI-powered sentiment analysis, automated responses, and reputation tracking. Coming soon to SERPfinity." />
        <meta name="keywords" content="review management, reputation management, review monitoring, sentiment analysis, AI review responses, Google reviews, Yelp reviews, online reputation, review generation, customer feedback" />
        <link rel="canonical" href="https://thedigitalhall.club/review-management" />
        <meta property="og:title" content="Review Management - AI-Powered Reputation Monitoring | SERPfinity" />
        <meta property="og:description" content="Monitor, analyze, and respond to customer reviews across 50+ platforms with AI-powered sentiment analysis and reputation tracking." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thedigitalhall.club/review-management" />
        <meta property="og:site_name" content="SERPfinity" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Review Management - AI-Powered Reputation Monitoring | SERPfinity" />
        <meta name="twitter:description" content="Monitor, analyze, and respond to customer reviews across 50+ platforms with AI-powered sentiment analysis and reputation tracking." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50" role="main">
        {/* Hero / Coming Soon Banner */}
        <header className="bg-gradient-to-r from-blue-900 via-indigo-800 to-purple-800 text-white">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2" role="list">
              <li><a href="/dashboard" className="text-blue-200 hover:text-white transition-colors">Dashboard</a></li>
              <li aria-hidden="true"><span className="text-blue-300">/</span></li>
              <li><a href="/listing-management" className="text-blue-200 hover:text-white transition-colors">Local SEO</a></li>
              <li aria-hidden="true"><span className="text-blue-300">/</span></li>
              <li aria-current="page"><span className="text-white font-medium">Review Management</span></li>
            </ol>
          </nav>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-500 text-gray-900 font-bold text-sm px-4 py-1.5 rounded-full mb-6" role="status">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Coming Soon
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Review Management
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              AI-powered review monitoring, sentiment analysis, and reputation management across 50+ platforms. Take control of your online reputation with intelligent automation.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium">
              <span className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                50+ Review Platforms
              </span>
              <span className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                AI-Powered Responses
              </span>
              <span className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                Sentiment Analysis
              </span>
            </div>
          </div>
        </header>

        {/* Product Overview Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" aria-labelledby="overview-heading">
          <div className="text-center mb-12">
            <h2 id="overview-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Why Review Management Matters for SEO
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Online reviews directly impact your search rankings, click-through rates, and customer trust. Businesses with strong review profiles see up to 270% higher conversion rates. SERPfinity Review Management gives you the tools to build, monitor, and leverage your online reputation for maximum search visibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-white rounded-xl border border-gray-200 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-700 rounded-full mb-4" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Search Ranking Impact</h3>
              <p className="text-gray-600">Reviews are a top local SEO ranking factor. Google considers review quantity, quality, velocity, and diversity when determining local pack and organic positions.</p>
            </article>

            <article className="bg-white rounded-xl border border-gray-200 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 text-green-700 rounded-full mb-4" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Conversion Rate Boost</h3>
              <p className="text-gray-600">Star ratings appear directly in search results and Google Ads, dramatically increasing click-through rates and driving more qualified traffic to your business.</p>
            </article>

            <article className="bg-white rounded-xl border border-gray-200 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 text-purple-700 rounded-full mb-4" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Customer Trust Signal</h3>
              <p className="text-gray-600">93% of consumers read online reviews before purchasing. A proactive review management strategy builds trust, addresses concerns, and showcases your commitment to customer satisfaction.</p>
            </article>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-white border-t border-b border-gray-200 py-12 sm:py-16" aria-labelledby="features-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 id="features-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Powerful Features Coming to Review Management
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Everything you need to monitor, manage, and grow your online reputation from a single platform.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {FEATURES.map((feature) => (
                <article
                  key={feature.id}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all focus-within:ring-2 focus-within:ring-blue-500"
                  tabIndex={0}
                  aria-labelledby={`feature-${feature.id}-title`}
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 text-blue-700 rounded-xl mb-4" aria-hidden="true">
                    {feature.icon}
                  </div>
                  <h3 id={`feature-${feature.id}-title`} className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{feature.description}</p>
                  <ul className="space-y-1.5" aria-label={`Benefits of ${feature.title}`}>
                    {feature.benefits.map((b, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {b}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Supported Platforms */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" aria-labelledby="platforms-heading">
          <div className="text-center mb-10">
            <h2 id="platforms-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Supported Review Platforms
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Aggregate reviews from all the platforms that matter to your business, with more being added regularly.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" role="list" aria-label="Supported review platforms">
            {PLATFORMS.map((p, i) => (
              <div
                key={i}
                role="listitem"
                className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg py-3 px-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: p.color }}
                  aria-hidden="true"
                />
                <span className="text-sm font-medium text-gray-700 truncate">{p.name}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">...and 40+ more platforms coming at launch</p>
        </section>

        {/* Early Access / Notification Form */}
        <section className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white py-12 sm:py-16" aria-labelledby="notify-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 id="notify-heading" className="text-2xl sm:text-3xl font-bold mb-4">
              Be the First to Know When We Launch
            </h2>
            <p className="text-blue-200 text-lg mb-8">
              Join the early access waitlist and receive exclusive launch pricing, beta access, and product updates delivered straight to your inbox.
            </p>

            {submitted ? (
              <div className="bg-green-500/20 border border-green-400 rounded-xl p-6" role="alert">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-bold mb-2">You are on the List!</h3>
                <p className="text-green-200">We will notify you as soon as Review Management launches. Check your inbox for a confirmation email.</p>
              </div>
            ) : (
              <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" aria-label="Early access notification signup">
                <label htmlFor="notify-email" className="sr-only">Email address for early access</label>
                <input
                  id="notify-email"
                  type="email"
                  required
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  aria-describedby="notify-desc"
                />
                <span id="notify-desc" className="sr-only">Enter your email to join the Review Management early access waitlist</span>
                <button
                  type="submit"
                  className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-6 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2 focus:ring-offset-blue-900"
                >
                  Notify Me
                </button>
              </form>
            )}
          </div>
        </section>

        {/* How It Will Work Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" aria-labelledby="how-it-works-heading">
          <h2 id="how-it-works-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            How Review Management Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Connect Platforms", desc: "Link your review profiles from Google, Yelp, Facebook, and 50+ other platforms with one-click authentication." },
              { step: "2", title: "Monitor Reviews", desc: "Receive real-time alerts for new reviews. See all feedback across every platform in a single unified dashboard." },
              { step: "3", title: "Analyze Sentiment", desc: "AI automatically classifies reviews, extracts key themes, and identifies trends to inform your reputation strategy." },
              { step: "4", title: "Respond and Grow", desc: "Generate AI-powered responses, run review acquisition campaigns, and track your reputation score improvement." }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 text-white font-bold text-lg rounded-full mb-4" aria-hidden="true">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-white border-t border-gray-200 py-12 sm:py-16" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 id="faq-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3" role="list" aria-label="Frequently asked questions about Review Management">
              {FAQ_ITEMS.map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" role="listitem">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
                    aria-expanded={openFaq === i}
                    aria-controls={`faq-answer-${i}`}
                    id={`faq-question-${i}`}
                  >
                    <span className="pr-4">{item.q}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div
                      id={`faq-answer-${i}`}
                      role="region"
                      aria-labelledby={`faq-question-${i}`}
                      className="px-5 pb-4 text-gray-600 leading-relaxed"
                    >
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center" aria-labelledby="cta-heading">
          <h2 id="cta-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Ready to Take Control of Your Online Reputation?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Review Management is coming soon to SERPfinity. In the meantime, explore our existing tools to boost your search visibility and optimize your content for both traditional and AI-powered search engines.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/content-audit"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Try Content Audit
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-bold px-6 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Go to Dashboard
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default ReviewManagement;
